import React, { useEffect, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Container,
  Avatar,
  Button,
  Divider,
  Card,
  CardContent,
} from "@mui/material";
import _ from "lodash";
import { useNavigate } from "react-router-dom";

import CustomLayout from "views/CustomLayout";
import axiosInstance from "configs/axiosConfig";
import Loader from "components/Loader";
import { APIRouteConstants } from "constants/routeConstants";

import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";

function TabPanel({ children, value, index }) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

const buildShortcutItems = (navigate, setTabIndex) => [
  {
    icon: <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />,
    title: "My Orders",
    description: "View and track your orders",
    action: () => navigate("/order-history"),
  },
  {
    icon: <LocationOnOutlinedIcon sx={{ fontSize: 18 }} />,
    title: "Addresses",
    description: "Manage your addresses",
    action: () => setTabIndex(1),
  },
];

const UserSetting = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [userDetail, setUserDetail] = useState({});
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const shortcutItems = buildShortcutItems(navigate, setTabIndex);

  const getUserDetails = async () => {
    setLoader(true);
    let res = await axiosInstance.get(APIRouteConstants.DASHBOARD.GET_USER_DETAIL);
    let { data } = res;
    if (data && !_.isEmpty(data.data)) {
      setUserDetail(data.data);
    }
    setLoader(false);
  };

  useEffect(() => {
    getUserDetails();
  }, []);

  const firstName = _.get(userDetail, "first_name", "");
  const lastName = _.get(userDetail, "last_name", "");
  const fullName = _.compact([firstName, lastName]).join(" ") || "User";
  const email = _.get(userDetail, "email", "");
  const countryCode = _.get(userDetail, "country_code", "");
  const mobile = _.get(userDetail, "mobile_number", "");
  const phone = countryCode && mobile ? `+${countryCode} ${mobile}` : mobile;
  const avatarLetter = (firstName || "U")[0].toUpperCase();

  return (
    <CustomLayout>
      <Loader open={loader} />
      <Container maxWidth="lg" disableGutters>
        <Box
          sx={{
            mt: { xs: "160px", md: "110px" },
            mb: 5,
            px: { xs: 2, sm: 2, md: 3 },
          }}
        >
          {/* Profile Header Card */}
          <Card
            sx={{
              mb: 3,
              borderRadius: "16px",
              boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              border: "1px solid #e5e7eb",
            }}
          >
            <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
              <Grid container alignItems="center">
                {/* Avatar + Info */}
                <Grid item xs={12} sm={7}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                    <Avatar
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: "#f0ebe3",
                        color: "#3d3d3d",
                        fontSize: "1.4rem",
                        fontWeight: 600,
                      }}
                    >
                      {avatarLetter}
                    </Avatar>
                    <Box>
                      <Typography
                        sx={{ fontWeight: 700, fontSize: "1rem", mb: 0.4, textTransform: "capitalize" }}
                      >
                        {fullName}
                      </Typography>
                      {email && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6, mb: 0.3 }}>
                          <EmailOutlinedIcon sx={{ fontSize: 13, color: "#6b7280" }} />
                          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                            {email}
                          </Typography>
                        </Box>
                      )}
                      {phone && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.6 }}>
                          <PhoneOutlinedIcon sx={{ fontSize: 13, color: "#6b7280" }} />
                          <Typography sx={{ fontSize: "0.75rem", color: "#6b7280" }}>
                            {phone}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Grid>

                {/* Vertical divider */}
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ display: { xs: "none", sm: "block" }, mx: 2, borderColor: "#e5e7eb" }}
                />

                {/* Action Buttons */}
                <Grid item xs={12} sm="auto" sx={{ flexGrow: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      alignItems: { xs: "flex-start", sm: "flex-end" },
                      mt: { xs: 2, sm: 0 },
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                      sx={{
                        borderRadius: "8px",
                        borderColor: "#d1d5db",
                        color: "#374151",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        px: 2,
                        py: 0.6,
                        width: { xs: "100%", sm: "150px" },
                        "&:hover": { borderColor: "#374151", background: "#f9fafb" },
                      }}
                    >
                      Edit Profile
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Inventory2OutlinedIcon sx={{ fontSize: 15 }} />}
                      onClick={() => navigate("/order-history")}
                      sx={{
                        borderRadius: "8px",
                        borderColor: "#d1d5db",
                        color: "#374151",
                        textTransform: "none",
                        fontWeight: 500,
                        fontSize: "0.75rem",
                        px: 2,
                        py: 0.6,
                        width: { xs: "100%", sm: "150px" },
                        "&:hover": { borderColor: "#374151", background: "#f9fafb" },
                      }}
                    >
                      View Orders
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Tabs */}
          <Box sx={{ borderBottom: "1px solid #e5e7eb", mb: 3 }}>
            <Tabs
              value={tabIndex}
              onChange={(_, v) => setTabIndex(v)}
              indicatorColor="primary"
              textColor="primary"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "0.8rem",
                  color: "#6b7280",
                  minHeight: "40px",
                },
                "& .Mui-selected": {
                  color: "#111827",
                  fontWeight: 600,
                },
              }}
            >
              <Tab icon={<PersonOutlineIcon />} iconPosition="start" label="Profile" />
              <Tab icon={<LocationOnOutlinedIcon />} iconPosition="start" label="Addresses" />
            </Tabs>
          </Box>

          {/* Profile Tab */}
          <TabPanel value={tabIndex} index={0}>
            <Grid container spacing={3} alignItems="stretch">
              {/* Personal Information */}
              <Grid item xs={12} sm={7}>
                <Card
                  sx={{
                    borderRadius: "12px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    border: "1px solid #e5e7eb",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2.5,
                      }}
                    >
                      <Typography sx={{ fontWeight: 600, fontSize: "0.85rem" }}>
                        Personal Information
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          color: "#6b7280",
                          "&:hover": { color: "#111827" },
                        }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 13 }} />
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                          Edit
                        </Typography>
                      </Box>
                    </Box>

                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", mb: 0.4, fontWeight: 500 }}>
                          First Name
                        </Typography>
                        <Typography sx={{ fontSize: "0.82rem", color: "#111827", fontWeight: 500, textTransform: "capitalize" }}>
                          {firstName || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", mb: 0.4, fontWeight: 500 }}>
                          Last Name
                        </Typography>
                        <Typography sx={{ fontSize: "0.82rem", color: "#111827", fontWeight: 500, textTransform: "capitalize" }}>
                          {lastName || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", mb: 0.4, fontWeight: 500 }}>
                          Email
                        </Typography>
                        <Typography sx={{ fontSize: "0.82rem", color: "#111827", fontWeight: 500 }}>
                          {email || "—"}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af", mb: 0.4, fontWeight: 500 }}>
                          Contact Number
                        </Typography>
                        <Typography sx={{ fontSize: "0.82rem", color: "#111827", fontWeight: 500 }}>
                          {phone || "—"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Account Shortcuts */}
              <Grid item xs={12} sm={5}>
                <Card
                  sx={{
                    borderRadius: "12px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                    border: "1px solid #e5e7eb",
                    height: "100%",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 1.5 }}>
                      Account Shortcuts
                    </Typography>

                    {shortcutItems.map((item, i) => (
                      <React.Fragment key={item.title}>
                        <Box
                          onClick={() => item.action()}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            py: 1.5,
                            cursor: "pointer",
                          }}
                        >
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: "8px",
                              background: "#f3f4f6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#374151",
                              flexShrink: 0,
                            }}
                          >
                            {item.icon}
                          </Box>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: "#111827" }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ fontSize: "0.7rem", color: "#9ca3af" }}>
                              {item.description}
                            </Typography>
                          </Box>
                          <ChevronRightIcon sx={{ color: "#9ca3af", fontSize: 16 }} />
                        </Box>
                        {i < shortcutItems.length - 1 && (
                          <Divider sx={{ borderColor: "#f3f4f6" }} />
                        )}
                      </React.Fragment>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Addresses Tab */}
          <TabPanel value={tabIndex} index={1}>
            <Card
              sx={{
                borderRadius: "16px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                border: "1px solid #e5e7eb",
              }}
            >
              <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                <Typography sx={{ fontWeight: 600, fontSize: "0.85rem", mb: 0.8 }}>
                  Addresses
                </Typography>
                <Typography sx={{ fontSize: "0.75rem", color: "#9ca3af" }}>
                  No address found. Coming soon — you'll be able to add or edit addresses.
                </Typography>
              </CardContent>
            </Card>
          </TabPanel>
        </Box>
      </Container>
    </CustomLayout>
  );
};

export default UserSetting;
